import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import './css/HeroSection.css'
import { fetchQuickSearchSuggestions } from '../../../services/searchApi'
import { UNIT_TYPES } from '../../../services/unitsApi'
import { getPublicationTypeLabel } from '../../../constants/publicationTypes'
import { resolveLocationLabel } from '../../../services/unlocodeService'
import { ciudades } from '../data'
import Iridescence from './Iridescence'

const STOP_WORDS = new Set([
  'de',
  'del',
  'la',
  'el',
  'los',
  'las',
  'una',
  'un',
  'y',
  'o',
  'en',
  'con',
  'para',
  'por',
  'al',
])

const normalizeText = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')

const tokenize = value =>
  normalizeText(value)
    .replace(/[^a-z0-9\s]/gi, ' ')
    .split(/\s+/)
    .filter(token => {
      if (!token) return false
      if (/\d/.test(token)) return true
      if (STOP_WORDS.has(token)) return false
      return token.length > 2
    })

const matchesNormalizedLabel = (labelNormalized, normalizedTerm) =>
  labelNormalized === normalizedTerm ||
  labelNormalized.includes(normalizedTerm) ||
  normalizedTerm.includes(labelNormalized)

const normalizeSearchPhrase = value =>
  normalizeText(value)
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const simplifyLocationLabel = label => {
  if (!label) return ''
  const raw = String(label)
  if (!raw.trim()) return ''
  const [firstChunk] = raw.split(',')
  const [hyphenChunk] = firstChunk.split(' - ')
  const [pipeChunk] = hyphenChunk.split('|')
  return pipeChunk.replace(/\s+/g, ' ').trim()
}

const FEATURED_LOCATION_ALIASES = [
  ...(Array.isArray(ciudades)
    ? ciudades.map(city => ({ label: city.name, keywords: [city.name] }))
    : []),
  {
    label: 'Ciudad de México',
    keywords: ['Ciudad de México', 'Ciudad de Mexico', 'CDMX', 'Mexico City'],
  },
  {
    label: 'Lázaro Cárdenas',
    keywords: ['Lázaro Cárdenas', 'Lazaro Cardenas'],
  },
  {
    label: 'Querétaro',
    keywords: ['Querétaro', 'Queretaro'],
  },
  {
    label: 'Guadalajara',
    keywords: ['Guadalajara'],
  },
  {
    label: 'Saltillo',
    keywords: ['Saltillo'],
  },
  {
    label: 'Puebla',
    keywords: ['Puebla'],
  },
  {
    label: 'Toluca',
    keywords: ['Toluca'],
  },
  {
    label: 'Tijuana',
    keywords: ['Tijuana'],
  },
  {
    label: 'Nuevo Laredo',
    keywords: ['Nuevo Laredo', 'Laredo'],
  },
  {
    label: 'Houston',
    keywords: ['Houston'],
  },
  {
    label: 'Los Ángeles',
    keywords: ['Los Angeles', 'Los Ángeles'],
  },
  {
    label: 'Miami',
    keywords: ['Miami'],
  },
]

const FEATURED_LOCATION_MATCHERS = Array.from(
  FEATURED_LOCATION_ALIASES.reduce((accumulator, entry) => {
    const keywords = Array.isArray(entry.keywords) ? entry.keywords : [entry.label]
    keywords.forEach(keyword => {
      const normalized = normalizeSearchPhrase(keyword)
      if (normalized && !accumulator.has(normalized)) {
        accumulator.set(normalized, {
          label: entry.label,
          normalized,
        })
      }
    })
    return accumulator
  }, new Map()).values()
)

const RAW_CATEGORY_MATCHERS = [
  {
    value: 'customs_agent',
    keywords: ['Agente aduanal', 'Agencia aduanal', 'Aduanal', 'Aduana', 'Servicio aduanal'],
  },
  {
    value: 'freight_forwarder',
    keywords: ['Freight forwarder', 'Forwarder', 'Operador logistico', 'Logistics operator'],
  },
  {
    value: 'product',
    keywords: ['Producto', 'Productos', 'Refacciones', 'Insumos', 'Almacen', 'Almacenes', 'Almacenaje', 'Warehouse'],
  },
  {
    value: 'service_ground_transport',
    keywords: ['Servicio terrestre', 'Transporte terrestre', 'Transporte de carga'],
  },
  {
    value: 'service_security',
    keywords: ['Servicio de custodia', 'Custodia', 'Seguridad', 'Escolta'],
  },
  {
    value: 'service_road_assistance',
    keywords: ['Asistencia vial', 'Gruas', 'Grúas', 'Road assistance'],
  },
]

const CATEGORY_MATCHERS = RAW_CATEGORY_MATCHERS.map(matcher => {
  const baseLabel = getPublicationTypeLabel(matcher.value)
  const keywords = Array.from(
    new Set(
      [baseLabel, ...(matcher.keywords || [])]
        .map(keyword => normalizeSearchPhrase(keyword))
        .filter(Boolean)
    )
  )
  return {
    value: matcher.value,
    label: baseLabel,
    keywords,
  }
})

const findFeaturedLocationEntry = normalizedPhrase => {
  if (!normalizedPhrase) return null
  const tokens = normalizedPhrase.split(' ').filter(Boolean)
  return (
    FEATURED_LOCATION_MATCHERS.find(entry => {
      if (entry.normalized === normalizedPhrase) return true
      if (entry.normalized.includes(normalizedPhrase) || normalizedPhrase.includes(entry.normalized)) {
        return true
      }
      if (entry.normalized.startsWith(normalizedPhrase) || normalizedPhrase.startsWith(entry.normalized)) {
        return true
      }
      return tokens.some(token => token.length >= 3 && entry.normalized.startsWith(token))
    }) || null
  )
}

const matchCategoryFromInput = (normalizedPhrase, tokenSet) => {
  if (!normalizedPhrase) return null
  const tokens = Array.from(tokenSet)
  return (
    CATEGORY_MATCHERS.find(matcher => {
      return matcher.keywords.some(keyword => {
        if (!keyword) return false
        if (keyword === normalizedPhrase) return true
        if (keyword.includes(' ')) {
          if (normalizedPhrase.includes(keyword) || keyword.includes(normalizedPhrase)) {
            return true
          }
          return tokens.some(token => token.length >= 3 && keyword.includes(token))
        }
        if (tokenSet.has(keyword)) return true
        if (keyword.startsWith(normalizedPhrase) || normalizedPhrase.startsWith(keyword)) {
          return true
        }
        return tokens.some(token => token.length >= 3 && (keyword.startsWith(token) || token.startsWith(keyword)))
      })
    }) || null
  )
}

const matchFeaturedLocation = normalizedPhrase => {
  const entry = findFeaturedLocationEntry(normalizedPhrase)
  return entry ? entry.label : null
}

const resolveLocationFilterLabel = async rawValue => {
  const trimmed = String(rawValue || '').trim()
  if (!trimmed) return null
  try {
    const resolved = await resolveLocationLabel(trimmed)
    const candidateLabel = resolved?.display || resolved?.name || resolved?.location || ''
    const simplified = simplifyLocationLabel(candidateLabel)
    if (simplified) {
      return simplified
    }
  } catch (error) {
    console.warn('No se pudo resolver la ubicación desde el buscador.', error)
  }
  const normalizedInput = normalizeSearchPhrase(trimmed)
  if (!normalizedInput) {
    return null
  }
  const fallback = matchFeaturedLocation(normalizedInput)
  return fallback || null
}

export default function HeroSection({ hasActiveSession = false }) {
  const [term, setTerm] = useState('')
  const [termSuggestions, setTermSuggestions] = useState([])
  const [termLoading, setTermLoading] = useState(false)
  const [termFocused, setTermFocused] = useState(false)
  const navigate = useNavigate()
  const termDebounceRef = useRef(null)
  const termBlurTimeoutRef = useRef(null)

  const unitTypeIndex = useMemo(() => {
    return UNIT_TYPES.map(option => {
      const labelTokens = tokenize(option.label)
      const valueTokens = tokenize(option.value)
      const searchPool = Array.from(new Set([...labelTokens, ...valueTokens]))
      return {
        ...option,
        normalizedLabel: normalizeText(option.label),
        searchTokens: searchPool,
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      window.clearTimeout(termDebounceRef.current)
      window.clearTimeout(termBlurTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    const query = term.trim()
    window.clearTimeout(termDebounceRef.current)

    if (query.length < 2) {
      setTermSuggestions([])
      setTermLoading(false)
      return
    }

    let ignore = false
    setTermLoading(true)
    termDebounceRef.current = window.setTimeout(() => {
      const loadSuggestions = async () => {
        try {
          const response = await fetchQuickSearchSuggestions(query, { limitPerType: 5 })
          if (ignore) return

          const normalizedQuery = normalizeText(query)
          const remoteList = Array.isArray(response) ? response : []
          const companyMatches = []
          const profileMatches = []
          let searchOption = null

          remoteList.forEach(item => {
            if (!item || !item.type) return
            if (item.type === 'search') {
              searchOption = {
                id: item.id,
                label: item.title || `Buscar "${query}"`,
                description: item.subtitle || 'Ver resultados completos en Marketplace',
                quickSearch: item.searchValue || query,
                type: 'search',
                href: item.href,
              }
              return
            }
            if (item.type === 'company' || item.type === 'profile') {
              const suggestion = {
                id: item.id,
                label: item.title || query,
                description: item.subtitle || '',
                type: item.type,
                quickSearch: item.title || query,
                href: item.href,
              }
              if (item.type === 'company') {
                companyMatches.push(suggestion)
              } else {
                profileMatches.push(suggestion)
              }
            }
          })

          const queryTokens = tokenize(normalizedQuery)
          const unitTypeMatches = unitTypeIndex
            .filter(option => {
              if (!queryTokens.length) return false
              if (option.normalizedLabel.includes(normalizedQuery)) return true
              return queryTokens.every(token =>
                option.searchTokens.some(candidate => candidate.includes(token))
              )
            })
            .slice(0, 5)
            .map(option => ({
              id: `unit-${option.value}`,
              label: option.label,
              description: 'Tipo de unidad',
              quickSearch: option.label,
              type: 'unitType',
              unitTypeValue: option.value,
            }))

          const normalizedSearchPhrase = normalizeSearchPhrase(query)
          const searchTokens = new Set(
            normalizedSearchPhrase ? normalizedSearchPhrase.split(' ').filter(Boolean) : []
          )

          const localSuggestions = []
          const categoryMatch = matchCategoryFromInput(normalizedSearchPhrase, searchTokens)
          if (categoryMatch) {
            localSuggestions.push({
              id: `category-${categoryMatch.value}`,
              label: categoryMatch.label,
              description: 'Filtrar resultados por categoría',
              type: 'categoryFilter',
              categoryLabel: categoryMatch.label,
              categorySlug: categoryMatch.value,
            })
          }

          const featuredLocationEntry = findFeaturedLocationEntry(normalizedSearchPhrase)
          if (featuredLocationEntry) {
            localSuggestions.push({
              id: `location-${featuredLocationEntry.label}`,
              label: featuredLocationEntry.label,
              description: 'Filtrar resultados por ubicación',
              type: 'locationFilter',
              locationLabel: featuredLocationEntry.label,
            })
          }

          const combined = [
            ...localSuggestions,
            ...companyMatches,
            ...profileMatches,
            ...unitTypeMatches,
          ]

          if (searchOption) {
            combined.push(searchOption)
          } else if (!combined.length) {
            combined.push({
              id: `search-${query}`,
              label: `Buscar "${query}" en Marketplace`,
              description: 'Ver resultados completos en Marketplace',
              quickSearch: query,
              type: 'search',
            })
          }

          setTermSuggestions(combined)
        } catch (error) {
          console.warn('No se pudieron cargar sugerencias de búsqueda.', error)
          if (!ignore) {
            setTermSuggestions([])
          }
        } finally {
          if (!ignore) {
            setTermLoading(false)
          }
        }
      }

      loadSuggestions()
    }, 240)

    return () => {
      ignore = true
      window.clearTimeout(termDebounceRef.current)
    }
  }, [term, unitTypeIndex])


  const findSuggestionMatch = (list, normalizedTerm) => {
    if (!normalizedTerm || !Array.isArray(list)) return null
    return list.find(item => {
      if (!item || !item.type || !item.label) return false
      const labelNormalized = normalizeText(item.label)
      if (
        item.type === 'company' ||
        item.type === 'profile' ||
        item.type === 'unitType' ||
        item.type === 'categoryFilter' ||
        item.type === 'locationFilter'
      ) {
        return matchesNormalizedLabel(labelNormalized, normalizedTerm)
      }
      return false
    })
  }

  const handleSearch = async () => {
    const params = new URLSearchParams()
    const trimmedTerm = term.trim()

    if (trimmedTerm) {
      const normalizedTerm = normalizeText(trimmedTerm)
      let directMatch = findSuggestionMatch(termSuggestions, normalizedTerm)

      if (!directMatch) {
        try {
          const fresh = await fetchQuickSearchSuggestions(trimmedTerm, { limitPerType: 5 })
          const list = Array.isArray(fresh) ? fresh : []
          setTermSuggestions(list)
          directMatch = findSuggestionMatch(list, normalizedTerm)
        } catch (error) {
          console.warn('No se pudieron cargar sugerencias para la búsqueda directa.', error)
        }
      }

      if (
        directMatch &&
        (directMatch.type === 'company' || directMatch.type === 'profile') &&
        directMatch.href
      ) {
        setTerm('')
        navigate(directMatch.href)
        return
      }

      let handled = false

      if (directMatch && directMatch.type === 'categoryFilter' && directMatch.categoryLabel) {
        setTerm('')
        params.set('category', directMatch.categoryLabel)
        if (directMatch.categorySlug) {
          params.set('categorySlug', directMatch.categorySlug)
        } else {
          params.delete('categorySlug')
        }
        handled = true
      } else if (
        directMatch &&
        directMatch.type === 'locationFilter' &&
        directMatch.locationLabel
      ) {
        setTerm('')
        params.set('location', directMatch.locationLabel)
        handled = true
      }

      if (directMatch && directMatch.type === 'unitType' && directMatch.unitTypeValue) {
        setTerm('')
        params.append('unitType', directMatch.unitTypeValue)
        handled = true
      }

      const normalizedPhrase = normalizeSearchPhrase(trimmedTerm)
      const tokenSet = new Set(
        normalizedPhrase ? normalizedPhrase.split(' ').filter(Boolean) : []
      )

      if (!handled) {
        const categoryMatch = matchCategoryFromInput(normalizedPhrase, tokenSet)
        if (categoryMatch) {
          setTerm('')
          params.set('category', categoryMatch.label)
          if (categoryMatch.value) {
            params.set('categorySlug', categoryMatch.value)
          } else {
            params.delete('categorySlug')
          }
          handled = true
        }
      }

      if (!handled) {
        const locationMatch = await resolveLocationFilterLabel(trimmedTerm)
        if (locationMatch) {
          setTerm('')
          params.set('location', locationMatch)
          handled = true
        }
      }

      if (!handled) {
        params.set('quickSearch', trimmedTerm)
      }
    } else if (term) {
      params.set('quickSearch', term)
    }

    const searchQuery = params.toString()
    navigate(searchQuery ? `/marketplace?${searchQuery}` : '/marketplace')
  }

  const shouldShowSuggestions = termFocused && (termLoading || termSuggestions.length > 0)

  const heroClassName = [
    'hero',
    hasActiveSession ? 'hero--compact' : '',
    shouldShowSuggestions ? 'hero--suggestions-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={heroClassName} aria-label="Búsqueda de transporte y logística">
      <Iridescence
        className="hero-iridescence"
        color={[0, 0.2, 0.5]}
        mouseReact={true}
        amplitude={0.05}
        speed={0.1}
        aria-hidden="true"
      />
      <span className="hero-glow" aria-hidden="true" />

      <div className="hero-inner">
        <div className="hero-column hero-copy">
          <span className="hero-badge hero-badge--pulse">
            {hasActiveSession ? 'Panel logístico activo' : 'Marketplace logístico #1'}
          </span>
          <h1 className="hero-title">
            {hasActiveSession ? 'Optimiza tus ' : 'La red logística de LatAm'}
            <span className="hero-title__highlight">
              {hasActiveSession ? 'operaciones' : ''}
            </span>
          </h1>
          <p className="hero-subtitle">
            {hasActiveSession
              ? 'Todo lo que necesitas para mover, abastecer y escalar tu logística.'
              : 'Conectando clientes y proveedores en minutos'}
          </p>

            <div className="hero-search" role="search">
              <div className="search-bar">
                <div className="hero-search-main">
                  <span className="hero-search-main__icon" aria-hidden="true">
                    <FaSearch />
                  </span>
                  <span className="hero-search-divider" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="¿Qué servicio logístico buscas hoy?"
                    value={term}
                  onChange={e => setTerm(e.target.value)}
                  onFocus={() => {
                    window.clearTimeout(termBlurTimeoutRef.current)
                    setTermFocused(true)
                  }}
                  onBlur={() => {
                    termBlurTimeoutRef.current = window.setTimeout(() => {
                      setTermFocused(false)
                    }, 120)
                  }}
                  aria-label="Buscar por palabra clave"
                  aria-autocomplete="list"
                  role="combobox"
                  aria-haspopup="listbox"
                  aria-expanded={shouldShowSuggestions}
                  aria-controls="hero-term-suggestions"
                  className="hero-search-main__input"
                />
                {shouldShowSuggestions && (
                  <div className="hero-suggestions" role="listbox" id="hero-term-suggestions">
                    {termLoading ? (
                      <div className="hero-suggestions__status">Buscando...</div>
                    ) : (
                      termSuggestions.map(suggestion => (
                        <button
                          type="button"
                          key={suggestion.id}
                          className="hero-suggestion"
                          onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                            setTermFocused(false)
                            setTermSuggestions([])
 
                            if (
                              (suggestion.type === 'company' || suggestion.type === 'profile') &&
                              suggestion.href
                            ) {
                              setTerm('')
                              navigate(suggestion.href)
                              return
                            }

                            const params = new URLSearchParams()
                            if (suggestion.type === 'categoryFilter' && suggestion.categoryLabel) {
                              setTerm('')
                              params.set('category', suggestion.categoryLabel)
                              if (suggestion.categorySlug) {
                                params.set('categorySlug', suggestion.categorySlug)
                              } else {
                                params.delete('categorySlug')
                              }
                            } else if (
                              suggestion.type === 'locationFilter' &&
                              suggestion.locationLabel
                            ) {
                              setTerm('')
                              params.set('location', suggestion.locationLabel)
                            } else if (suggestion.type === 'unitType' && suggestion.unitTypeValue) {
                              setTerm('')
                              params.append('unitType', suggestion.unitTypeValue)
                            } else if (suggestion.quickSearch) {
                              setTerm(suggestion.quickSearch)
                              params.set('quickSearch', suggestion.quickSearch)
                            }
                            const searchQuery = params.toString()
                            navigate(searchQuery ? `/marketplace?${searchQuery}` : '/marketplace')
                          }}
                        >
                          <span className="hero-suggestion__label">{suggestion.label}</span>
                          {suggestion.description && (
                            <span className="hero-suggestion__description">
                              {suggestion.description}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                    {!termLoading && !termSuggestions.length && (
                      <div className="hero-suggestions__status">Sin coincidencias</div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="hero-search-main__cta"
                  onClick={handleSearch}
                  aria-label="Buscar servicios logísticos"
                >
                  Buscar
                </button>
              </div>

            </div>
            <p className="hero-search-help">
              Descubre cotizaciones confiables, rutas activas y capacidad disponible.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
